import { Box, InputLabel, Theme, InputLabelProps, TextField } from '@mui/material';
import type { SystemStyleObject } from '@mui/system';
import DatePicker, { DatePickerProps } from '@mui/lab/DatePicker';
import AdapterDateDns from '@mui/lab/AdapterDateFns';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import enCA from 'date-fns/locale/en-CA';
import { useEffect, useState } from 'react';
import isValid from 'date-fns/isValid';
import { StrOrNum } from '../../types/util';

interface DateInputProps {
  id?: string;
  label?: string;
  sx?: SystemStyleObject<Theme>;
  required?: boolean;
  placeholder?: string;
  LabelProps?: InputLabelProps;
  FieldProps?: DatePickerProps['InputProps'];
  error?: string;
  DatePickerProps?: Partial<DatePickerProps>;
  value?: StrOrNum;
  onChange?: (value: string) => void;
}
export const DateInput: React.FC<DateInputProps> = ({
  id,
  label,
  sx,
  LabelProps,
  FieldProps,
  required = false,
  placeholder,
  error,
  DatePickerProps,
  value,
  onChange,
}) => {
  const [open, setOpen] = useState(false);
  const [dateValue, setDateValue] = useState<Date | null>(value ? new Date(value.toString()) : null);
  const views: DatePickerProps['views'] = DatePickerProps?.views || ['year', 'month', 'day'];

  const onDatePicked = (value: unknown) => {
    if (value instanceof Date) {
      if (!isNaN(value.getTime()) && onChange) {
        const dateValues: StrOrNum[] = [];
        if (views.includes('year')) {
          dateValues.push(value.getFullYear());
        }
        if (views.includes('month')) {
          const month = parseInt((value.getMonth() + 1).toString());
          dateValues.push(month < 10 ? `0${month}` : month);
        }
        if (views.includes('day')) {
          const day = parseInt(value.getDate().toString());
          dateValues.push(day < 10 ? `0${day}` : day);
        }
        onChange(dateValues.join('-'));
      }
      setDateValue(value);
    } else {
      setDateValue(null);
      if (onChange) onChange('');
    }
  };

  const getMask = (): string => {
    const masks: string[] = [];
    if (views.includes('year')) {
      masks.push('____');
    }
    if (views.includes('month')) {
      masks.push('__');
    }
    if (views.includes('day')) {
      masks.push('__');
    }
    return masks.join('-');
  };
  const mask = getMask();

  const openPicker = () => setOpen(true);
  const closePicker = () => setOpen(false);

  useEffect(() => {
    if (value && isValid(new Date(value.toString()))) {
      setDateValue(new Date(value.toString()));
    } else {
      setDateValue(null);
    }
  }, [value]);

  return (
    <Box sx={[{ mb: 2.5 }, sx || {}]}>
      {label && (
        <InputLabel {...LabelProps} htmlFor={id} required={required}>
          {label}
        </InputLabel>
      )}
      <LocalizationProvider dateAdapter={AdapterDateDns} locale={enCA}>
        <DatePicker
          open={open}
          views={views}
          mask={mask}
          onOpen={openPicker}
          onClose={closePicker}
          value={dateValue}
          allowSameDateSelection
          onChange={onDatePicked}
          onAccept={onDatePicked}
          {...DatePickerProps}
          InputProps={{ required, onClick: openPicker, ...FieldProps }}
          renderInput={(params) => {
            return (
              <TextField
                sx={{ width: 1 }}
                {...params}
                required={required}
                id={id}
                inputProps={{ ...params.inputProps, placeholder }}
                error={Boolean(error)}
                helperText={error}
              />
            );
          }}
        />
      </LocalizationProvider>
    </Box>
  );
};
